<template>
  <div class="list-root">
    <div class="title">
      <div class="edit-column">
        <div>{{ $t("manuscriptPage.manuscriptFolderList.folder") }}</div>
        <div v-if="isInsert" class="sorting" @click="completeSetFolder">
          {{ $t("manuscriptPage.manuscriptFolderList.moveManuscript") }}
        </div>
        <div v-else class="sorting" @click="switchEditMode()">
          {{ isEditMode ? $t("common.save") : $t("manuscriptPage.manuscriptFolderList.sort") }}
        </div>
      </div>
    </div>
    <div class="list" v-if="items">
      <list-folder-item
        v-if="!isInsert && !isEditMode"
        :label="$t('manuscriptPage.manuscriptFolderList.allManuscripts')"
        :count="manuscriptsCount"
        :onSelectCallback="() => selectFolder('')"
        :disabledMenu="true"
      />
      <div class="folder-select-description" v-if="isInsert">
        {{ $t("manuscriptPage.manuscriptFolderList.selectFolderDescription") }}
      </div>
      <sortable-list
        class="list-container"
        :items.sync="folders"
        keyAttr="manuscriptFolderId"
        :disabled="!isEditMode"
        :extra="isEditMode"
      >
        <template v-slot:default="{ item, extra }">
          <list-folder-item
            :label="item.name"
            :count="item.manuscriptKeys.length"
            :isSorting="extra"
            :isSelecting="isInsert"
            :onSelectCallback="() => selectFolder(item.manuscriptFolderId)"
            :isChecked="isSelectedFolder(item.manuscriptFolderId)"
            :onCheckCallback="() => onToggleInsertFolder(item.manuscriptFolderId)"
            :onRenameCallback="() => renameManuscriptFolder(item.manuscriptFolderId)"
            :onDeleteCallback="() => deleteManuscriptFolder(item.manuscriptFolderId)"
          />
        </template>
      </sortable-list>
    </div>
    <button v-show="!isEditMode && !isInsert" class="create" @click="createManuscriptFolder">
      {{ $t("manuscriptPage.manuscriptFolderList.newCreate") }}
    </button>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { ManuscriptFolder } from "@/lib/models";
import SortableList from "@/components/molecules/SortableList.vue";
import ListFolderItem from "@/components/molecules/ListFolderItem.vue";
import { Dialog } from "@/lib/utils";
import FolderRenameDialog, { FolderRenameDialogProps } from "@/components/ui/novel/FolderRenameDialog.vue";
import BreakingChangeConfirm, { BreakingChangeConfirmProps } from "@/components/ui/dialogs/BreakingChangeConfirm.vue";

export default Vue.extend({
  components: { SortableList, ListFolderItem },
  props: {
    novelId: String, // 小説ID
    items: Array as PropType<ManuscriptFolder[]>, // 原稿フォルダ
    manuscriptsCount: Number, // フォルダに含まれる原稿の数
    isInsert: Boolean, // 状態管理用。原稿にフォルダを設定する状態かどうかを管理
    selectedManuscript: Object, // フォルダを設定しようとしている原稿
  },
  data() {
    return {
      folders: this.items || [],
      isEditMode: false,
      selectedInsertFolderIds: [],
    };
  },
  watch: {
    items() {
      this.folders = this.items;
    },
    isInsert: {
      handler(isInsert: boolean) {
        if (!isInsert) return;

        const folders = this.foldersBelongManuscript(this.selectedManuscript.item.key) as ManuscriptFolder[];
        this.selectedInsertFolderIds = folders.map((folder) => folder.manuscriptFolderId);
      },
      immediate: true,
    },
  },
  computed: {
    foldersBelongManuscript() {
      return (manuscriptKey: string) => this.$store.getters["manuscriptModule/foldersBelongManuscript"](manuscriptKey);
    },
    isSelectedFolder() {
      return (manuscriptFolderId: string) => this.selectedInsertFolderIds.includes(manuscriptFolderId);
    },
  },
  methods: {
    selectFolder(manuscriptFolderId: string) {
      this.$emit("selectedFolder", manuscriptFolderId);
    },
    async completeSetFolder() {
      const { folders, selectedInsertFolderIds, selectedManuscript } = this;
      const updatedFolders = (folders as ManuscriptFolder[]).map((folder) => {
        const target = folder;
        if (selectedInsertFolderIds.includes(target.manuscriptFolderId)) {
          target.manuscriptKeys = [...new Set([...target.manuscriptKeys, selectedManuscript.item.key])];
        } else {
          target.manuscriptKeys = target.manuscriptKeys.filter((key) => key !== selectedManuscript.item.key);
        }
        return target;
      });

      await this.updateManuscriptFolder(updatedFolders);
      this.$emit("clickCompleteSetFolder");
    },
    async createManuscriptFolder() {
      const { novelId } = this;
      const name = this.$t("manuscriptPage.manuscriptFolderList.newFolder") as string;

      await this.$store.dispatch("manuscriptModule/createFolder", { novelId, name });
    },
    async deleteManuscriptFolder(key: string) {
      const confirmDialog = new Dialog(BreakingChangeConfirm);
      const data: BreakingChangeConfirmProps = {
        title: this.$t("manuscriptPage.manuscriptFolderList.deleteConfirm.title") as string,
        content: this.$t("manuscriptPage.manuscriptFolderList.deleteConfirm.content") as string,
        change: this.$t("manuscriptPage.manuscriptFolderList.deleteConfirm.deleteButton") as string,
        cancel: this.$t("manuscriptPage.manuscriptFolderList.deleteConfirm.cancelButton") as string,
      };

      const result = await confirmDialog.show(data);

      if (!result) {
        return;
      }

      const { novelId } = this;
      const manuscriptFolders = (this.folders as ManuscriptFolder[]).filter(
        (folder) => folder.manuscriptFolderId !== key
      );
      await this.$store.dispatch("manuscriptModule/updateFolder", { novelId, manuscriptFolders });

      this.$notify({
        title: this.$t("manuscriptPage.manuscriptFolderList.deleteNotification") as string,
        type: "error",
      });
    },
    async switchEditMode() {
      // 編集終了を押した場合、かつ変更していた場合
      if (this.isEditMode) {
        await this.updateManuscriptFolder(this.folders);
      }

      this.isEditMode = !this.isEditMode;
    },
    async updateManuscriptFolder(folders: ManuscriptFolder[]) {
      const { novelId } = this;

      // 変なデータが有ったらこっそり修正する
      const manuscriptFolders = folders.map((folder) => {
        const manuscriptKeys = folder.manuscriptKeys
          .filter((key) => key != null)
          .filter((el, index, selfArray) => selfArray.indexOf(el) === index); // 重複Keyは排除
        return { ...folder, manuscriptKeys };
      });

      await this.$store.dispatch("manuscriptModule/updateFolder", { novelId, manuscriptFolders });
    },
    async renameManuscriptFolder(key: string) {
      const renameCallback = async (name: string) => {
        const manuscriptFolders = (this.folders as ManuscriptFolder[]).map((folder) => {
          const item = folder;
          if (item.manuscriptFolderId === key) item.name = name;
          return item;
        });
        this.updateManuscriptFolder(manuscriptFolders);
      };
      const { name } = (this.folders as ManuscriptFolder[]).find((folder) => folder.manuscriptFolderId === key)!;

      const confirmDialog = new Dialog(FolderRenameDialog);
      const data: FolderRenameDialogProps = {
        name,
        renameCallback,
      };
      await confirmDialog.show(data);
    },
    onToggleInsertFolder(manuscriptFolderId: string) {
      if ((this.selectedInsertFolderIds as string[]).includes(manuscriptFolderId)) {
        this.selectedInsertFolderIds = this.selectedInsertFolderIds.filter(
          (folderId: string) => folderId !== manuscriptFolderId
        );
      } else {
        this.selectedInsertFolderIds.push(manuscriptFolderId);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.list-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;

  @include mq(sp) {
    flex: 1;
    height: auto;
    overflow: hidden;
  }
}

.title {
  flex-shrink: 0;
  color: var(--color-text-normal);
  padding: 10px 15px;
  font-weight: var(--font-weight-bold);
  background-color: var(--color-surface-subtle);
  border-bottom: solid 1px var(--color-border);
}
.edit-column {
  display: flex;
  justify-content: space-between;
}
.sorting {
  color: var(--color-text-normal);
  cursor: pointer;
}

.list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  background-color: var(--color-background-normal);

  .folder-select-description {
    padding: 5px;
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-normal);
  }
}

.create {
  position: relative;
  flex-shrink: 0;
  height: 50px;
  width: 100%;
  padding: 10px;
  font-size: 1.2em;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border: none;
  cursor: pointer;
  transition: filter 0.3s ease;

  @include hover-overlay;
}

@mixin link {
  background: transparent;
  border: none;
  cursor: pointer;
}

.move {
  @include link;
  position: relative;

  position: relative;
  font-size: large;
  color: var(--color-text-low);

  @include hover-overlay;
}
</style>
